import React, { useContext, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { RadioButtonSquare } from '@/components/atoms/radio-button-square'
import { css } from '@emotion/core'
import { CustomerInfoInputs } from './customer-info-inputs'
import { SubmitHandler, useForm } from 'react-hook-form'
import { AlignmentReservationInput, AssignSpaceInput, NewReservationInput, SpaceReservationInput } from '@/models/space-manager'
import { NewSpace } from '@/models/space-manager/timeline'
import dayjs from 'dayjs'
import { CreateSpaceReservation, getSpaceByReservation } from '@/apis/aipass'
import { errorHandler } from '@/libs/errors'
import { SearchReservationEachRoom } from '@/models/reservation'
import { LoaderContextCreator } from '@/contexts/loader'
import { SpaceSearchReservationModal } from './space-search-reservation-modal'

interface SpaceReservationModalProps {
  onClose: (reservationId?: string) => void
  spaces: NewSpace
  date: dayjs.Dayjs
}
export const SpaceReservationModal: React.FC<SpaceReservationModalProps> = ({ date, onClose, spaces }) => {
  const [reservationsType, setReservationType] = useState<'lodging' | 'dayTrip'>()
  const [showSearch, setShowSearch] = useState(false)

  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const useFormReturn = useForm<NewReservationInput>({})
  const {
    handleSubmit,
    formState: { isValid },
  } = useFormReturn

  const assignSpaces: AssignSpaceInput[] = []
  Object.keys(spaces).forEach(spaceName => {
    spaces[spaceName].forEach(space => {
      const assign = {
        usageFrom: dayjs(`${space?.date?.format('YYYY-MM-DD')} ${space.usageTime.usageFrom}`).format('YYYY-MM-DD HH:mm:ss'),
        usageTo: dayjs(`${space?.date?.format('YYYY-MM-DD')} ${space.usageTime.usageTo}`).format('YYYY-MM-DD HH:mm:ss'),
        spaceId: space.spaceId,
        spaceIndex: space.spaceIndex,
        pax: space.pax,
        reservationUserName: '', // 宿泊連携or日帰りsubmit時に入れる
        answer: space.answer || '',
        spaceTypeName: spaceName,
      }
      assignSpaces.push(assign)
    })
  })

  const onDayTripCreate: SubmitHandler<NewReservationInput> = async submitData => {
    const newReservation: SpaceReservationInput = {
      name: submitData.newReservation.name || '',
      nameKana: submitData.newReservation.nameKana || '',
      email: submitData.newReservation.email || '',
      tel: submitData.newReservation.tel || '',
      address: submitData.newReservation.address || '',
      checkinDate: date.format('YYYY-MM-DD'),
      checkoutDate: date.format('YYYY-MM-DD'),
    }
    doSpaceCreate({
      newReservation,
      assignSpaces: assignSpaces.map(assign => ({
        ...assign,
        reservationUserName: submitData.newReservation.name || '',
      })),
    })
  }

  const onStayCreate = async (targetReservation: SearchReservationEachRoom) => {
    const fetchedReservationSpaces = await getSpaceByReservation(targetReservation.reservationId).then(res => res || [])
    const fetchedReservationSpaceDetails = fetchedReservationSpaces.flatMap(fetchedReservationSpace => fetchedReservationSpace.details)
    const alignmentData: AlignmentReservationInput = {
      reservationId: targetReservation.reservationId,
      guestRoomAssignId: targetReservation.guestRoomAssignId,
      assignSpaces: assignSpaces.map(assignSpace => {
        const sameSpace = fetchedReservationSpaceDetails.find(
          fetchedReservationSpaceDetail =>
            fetchedReservationSpaceDetail.reservedSpace.title.ja === assignSpace.spaceTypeName &&
            fetchedReservationSpaceDetail.usedAtFrom === assignSpace.usageFrom &&
            fetchedReservationSpaceDetail.usedAtTo === assignSpace.usageTo,
        )
        return {
          ...assignSpace,
          reservationUserName: sameSpace?.reservationUserName || targetReservation.reservationGuestName || '',
        }
      }),
    }
    doSpaceCreate(alignmentData)
  }

  const doSpaceCreate = async (alignmentData: NewReservationInput | AlignmentReservationInput) => {
    try {
      setIsLoading(true)
      const result = await CreateSpaceReservation(alignmentData)
      onClose(result.reservationId)
    } catch (error) {
      errorHandler(error)
      onClose()
    } finally {
      setShowSearch(false)
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal customCss={modalStyle} style={{ visibility: showSearch ? 'hidden' : 'visible' }}>
        <ModalHeader>{t('HotelCuicinUser')}</ModalHeader>
        <ModalBody>
          <div className="container">
            <div className="block">
              <div className="radio-group">
                <RadioButtonSquare
                  onChange={() => {
                    setShowSearch(true)
                    setReservationType('lodging')
                  }}
                  checked={reservationsType === 'lodging'}
                  value="lodging"
                  id="lodging"
                  text={t('Lodging')}
                />
                <RadioButtonSquare
                  onChange={() => setReservationType('dayTrip')}
                  checked={reservationsType === 'dayTrip'}
                  value="dayTrip"
                  id="dayTrip"
                  text={t('one day trip')}
                />
              </div>
            </div>

            <hr style={{ marginBlock: 30, border: 'none', backgroundColor: '#ccc' }} />

            <div className="block">{reservationsType === 'dayTrip' && <CustomerInfoInputs useFormReturn={useFormReturn} />}</div>
          </div>
        </ModalBody>
        {reservationsType === 'dayTrip' && (
          <form onSubmit={handleSubmit(onDayTripCreate)}>
            <ModalFooter>
              <Button buttonType={3} height="38px" width="110px" marginRight={16} onClick={() => onClose()}>
                {t('Cancel')}
              </Button>
              <Button buttonType={isValid ? 1 : 5} type="submit" height="38px" width="110px" isDisabled={!isValid}>
                {t('Create')}
              </Button>
            </ModalFooter>
          </form>
        )}
      </Modal>

      {showSearch && (
        <SpaceSearchReservationModal
          usageDay={date}
          onCancel={() => {
            setShowSearch(false)
            onClose()
          }}
          onSubmit={onStayCreate}
        />
      )}
    </>
  )
}

const modalStyle = css({
  top: '5%',
  height: 'auto',
  minHeight: 'auto',
  maxHeight: 'calc(100vh - 10%)',
  '.container': {
    paddingBlock: 30,
    border: '1px solid #ccc',
    borderRadius: 5,
    background: '#fff',
  },
  '.block': {
    paddingInline: 72,
  },
  '.radio-group': {
    display: 'flex',
    gap: 24,
    '> div': {
      width: 244,
      height: 43,
    },
  },
})
